import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { QUIET_QUERY_META } from '../lib/query-activity.ts'
import {
  fetchSearchAsk,
  MIN_ASK_LENGTH,
  normalizeAskQuery,
  type SearchAskAnswer
} from '../lib/search-ask.ts'

/** How long an unused answer stays cached. */
const ASK_CACHE_MS = 30 * 60 * 1000

/** The asynchronous suggestion state; keyword search never depends on it. */
export interface SearchAskState {
  status: 'idle' | 'loading' | 'ready' | 'hidden'
  answer: SearchAskAnswer | undefined
}

/** Debounce normalized questions and share their quiet, session-long cache. */
export function useSearchAsk(
  text: string,
  { debounceMs = 400 }: { debounceMs?: number } = {}
): SearchAskState {
  const normalized = normalizeAskQuery(text)
  const [question, setQuestion] = useState({ normalized, debounced: '' })
  if (question.normalized !== normalized) {
    setQuestion({ normalized, debounced: '' })
  }
  useEffect(() => {
    const timer = setTimeout(
      () => setQuestion({ normalized, debounced: normalized }),
      debounceMs
    )
    return () => clearTimeout(timer)
  }, [normalized, debounceMs])

  // Detach immediately while debouncing so a changed question aborts its old request.
  const key = normalized === question.normalized ? question.debounced : ''
  const enabled = key.length >= MIN_ASK_LENGTH && key.length <= 500
  const query = useQuery({
    queryKey: ['search-ask', key],
    queryFn: ({ signal }) => fetchSearchAsk(key, signal),
    enabled,
    retry: false,
    throwOnError: false,
    // Only a ranking is kept. Off, unavailable and a bad body are asked again
    // when the question returns, because each can clear within the session.
    staleTime: ({ state }) => (state.data?.status === 'ok' ? Infinity : 0),
    gcTime: ASK_CACHE_MS,
    refetchOnWindowFocus: false,
    meta: QUIET_QUERY_META
  })

  if (!enabled) return { status: 'idle', answer: undefined }
  if (query.isPending) return { status: 'loading', answer: undefined }
  if (query.isError) return { status: 'hidden', answer: undefined }
  return {
    status: query.data.status === 'ok' ? 'ready' : 'hidden',
    answer: query.data
  }
}
